import { useState, useEffect } from "react"
import { useDebounce } from "../../../hooks/useDebounce"
import MTable from "../../../components/mtable"
import InitialsAvatar from "../../../components/InitialsAvatar"
import { formatEnum } from "../../../utils/strings"
import { getStorage, STORAGE } from "../../../utils/storage"
import { api } from "../../../env"

const STATUS_FILTERS = [
    { label: 'All',      value: undefined },
    { label: 'Preview',  value: 'Preview' },
    { label: 'Proposed', value: 'Proposed' },
    { label: 'Accepted', value: 'Accepted' },
    { label: 'Rejected', value: 'Rejected' },
]

function matchStatusChipClass(status) {
    if (status === 'Accepted')  return 'primary-dark-chip'
    if (status === 'Rejected')  return 'error-chip'
    if (status === 'Proposed')  return 'primary-light-chip'
    return 'neutral-chip'
}

function MatchParticipant({ user }) {
    return (
        <div className="match-participant">
            <InitialsAvatar firstName={user?.firstName} lastName={user?.lastName} />
            <div style={{ minWidth: 0 }}>
                <p className="card-label medium ellipsis">{user?.firstName} {user?.lastName}</p>
                {user?.organization && <p className="card-label-s neutral ellipsis">{user.organization}</p>}
            </div>
        </div>
    )
}

export default function MatchesOverview({ programId }) {
    const isAdmin = getStorage(STORAGE.IS_ADMIN)
    const [confirmingId, setConfirmingId] = useState(null)
    const [statusFilter, setStatusFilter] = useState(undefined)
    const [search, setSearch] = useState('')
    const [params, setParams] = useState({})
    const debouncedSearch = useDebounce(search, 250)

    useEffect(() => {
        setParams({
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(statusFilter ? { matchStatus: statusFilter } : {})
        })
    }, [debouncedSearch, statusFilter])

    const handleUnmatch = (item) => {
        api.post(`programs/${programId}/matches/${item.id}/unmatch`)
            .then(() => window.location.reload())
            .catch(() => {})
    }

    const mapper = { columns: [
        {
            label: 'Mentor', width: '1.25fr', align: 'left',
            render: (item) => <MatchParticipant user={item.mentorEnrollment?.user} />
        },
        {
            label: 'Mentee', width: '2fr', align: 'center',
            render: (item) => <div className="flex justify-center"> <MatchParticipant user={item.menteeEnrollment?.user} /> </div> 
        },
        {
            label: 'Status', width: '1fr', 'align': 'center',
            render: (item) => (
                <div className="flex justify-center">
                    <span className={`chip scale-s transparent ${matchStatusChipClass(item.matchStatus)}`}>
                        {formatEnum(item.matchStatus)}
                    </span>
                </div>
            )
        },
        ...(isAdmin ? [{
            label: 'Actions', width: 'auto',
            render: (item) => (
                <div className="match-actions">
                    {confirmingId !== null ? (<>
                        <button
                            className={`small ${confirmingId === item.id ? 'outline' : 'text disabled'}`}
                            disabled={confirmingId !== item.id}
                            onClick={(e) => { e.stopPropagation(); confirmingId === item.id && setConfirmingId(null) }}
                        >Cancel</button>
                        <button
                            className={`small ${confirmingId === item.id ? 'solid-accent' : 'text disabled'}`}
                            disabled={confirmingId !== item.id}
                            onClick={(e) => { e.stopPropagation(); confirmingId === item.id && handleUnmatch(item) }}
                        >Confirm</button>
                    </>) : (
                        <button
                            className={`small ${item.matchStatus === 'Rejected' ? 'text disabled' : 'solid'}`}
                            disabled={item.matchStatus === 'Rejected'}
                            onClick={(e) => { e.stopPropagation(); item.matchStatus !== 'Rejected' && setConfirmingId(item.id) }}
                        >Unmatch</button>
                    )}
                </div>
            )
        }] : [])
    ]}

    return (
        <div>
            <div className="flex center space-between v-spacer-s">
                <div>
                    <p className="card-title">Pairs</p>
                    <p className="neutral card-label">Browse active mentor-mentee pairs.</p>
                </div>
            </div>

            <div className="card input-wrapper v-spacer-s">
                <span className="material-symbols-outlined icon input-icon">search</span>
                <input
                    placeholder="Search by mentor or mentee name…"
                    value={search}
                    className="w-100"
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="card flex gap-xs wrap v-spacer-s">
                {STATUS_FILTERS.map(f => (
                    <button
                        key={f.label}
                        className={`chip ${statusFilter === f.value ? 'solid' : 'outline'}`}
                        onClick={() => setStatusFilter(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <MTable
                path={`programs/${programId}/matches`}
                queryParams={params}
                mapper={mapper}
            />
        </div>
    )
}
